import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KbPage, StatusKind } from '../lib/contract';
import { findingStatusKind } from '../lib/contract';
import { apiFetch, useResource } from '../lib/api';
import { useSession } from '../lib/session';
import { Badge, Btn, Card, PageHead, SectionHead, SeverityBadge } from '../components/ui/primitives';
import { Loading } from '../components/ui/widgets';

interface ConsultFinding {
  cve: string;
  component: string;
  severity: StatusKind;
  isKev: boolean;
  art14: boolean;
  highEpss: boolean;
  epss: number;
}

interface ScanResult {
  name: string;
  scanned: number;
  applicable: number;
  kev: number;
  findings: ConsultFinding[];
}

const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

// Demo consult (fixtures mode) — KEVs first, then applicable CVEs by descending EPSS.
const DEMO: ScanResult = {
  name: 'Edge Gateway EG-500',
  scanned: 418,
  applicable: 46,
  kev: 2,
  findings: [
    { cve: 'CVE-2024-38063', component: 'tcp/ip-stack 2.1', severity: 'critical', isKev: true, art14: true, highEpss: false, epss: 0.94 },
    { cve: 'CVE-2025-1122', component: 'libmodbus 3.1.6', severity: 'high', isKev: true, art14: false, highEpss: false, epss: 0.71 },
    { cve: 'CVE-2023-4807', component: 'openssl 3.0.1', severity: 'high', isKev: false, art14: false, highEpss: true, epss: 0.62 },
    { cve: 'CVE-2024-2961', component: 'glibc 2.35', severity: 'medium', isKev: false, art14: false, highEpss: false, epss: 0.44 },
    { cve: 'CVE-2023-52425', component: 'expat 2.5.0', severity: 'medium', isKev: false, art14: false, highEpss: false, epss: 0.19 },
  ],
};

interface ApiScanFinding {
  cveId: string;
  component: string;
  componentVersion: string | null;
  severity: string | null;
  cvss: number | null;
  epss: number | null;
  isKev: boolean;
  applicability: string;
}

export function KnowledgeBase() {
  const navigate = useNavigate();
  const { session } = useSession();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [result, setResult] = useState<ScanResult | null>(USE_FIXTURES ? DEMO : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function scan(file: File) {
    setBusy(true);
    setError(null);
    try {
      const content = await file.text();
      const res = await apiFetch(session, '/api/kb-scan', {
        method: 'POST',
        body: JSON.stringify({ filename: file.name, content }),
      });
      if (!res.ok) {
        throw new Error(res.status === 400 ? 'That file does not parse as a CycloneDX/SPDX SBOM.' : `scan failed (${res.status})`);
      }
      const data = (await res.json()) as { findings: ApiScanFinding[]; kevCount: number; applicableCount: number };
      const findings: ConsultFinding[] = data.findings
        .map((f) => ({
          cve: f.cveId,
          component: `${f.component}${f.componentVersion ? ` ${f.componentVersion}` : ''}`,
          severity: findingStatusKind({ severity: f.severity, cvss: f.cvss, applicability: f.applicability }),
          isKev: f.isKev,
          art14: f.isKev,
          highEpss: (f.epss ?? 0) >= 0.5,
          epss: f.epss ?? 0,
        }))
        .sort((a, b) => (a.isKev !== b.isKev ? (a.isKev ? -1 : 1) : b.epss - a.epss));
      const cleanName = file.name.replace(/\.(cdx\.)?(json|xml|spdx)$/i, '');
      // Carry the scanned SBOM into "Add as a product" (sessionStorage — never uploaded until then).
      try { sessionStorage.setItem('bp.scan', JSON.stringify({ name: cleanName, filename: file.name, content })); } catch { /* >5MB: skip */ }
      setResult({
        name: cleanName,
        scanned: data.findings.length,
        applicable: data.applicableCount,
        kev: data.kevCount,
        findings,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const pct = (n: number, of: number) => (of > 0 ? `−${Math.round((1 - n / of) * 100)}%` : '—');

  return (
    <>
      <PageHead
        title="Knowledge Base"
        sub="Scan any SBOM against every feed — nothing is saved, nothing is tracked."
        action={<Badge kind="safe">✓ ephemeral · not added to your portfolio</Badge>}
      />

      <Card style={{ padding: 24 }}>
        <input
          ref={fileRef} type="file" accept=".json,.xml,.spdx" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void scan(f); e.target.value = ''; }}
        />
        <div
          className="dropzone" style={{ padding: 40, cursor: 'pointer' }}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void scan(f); }}
        >
          <div className="big">{busy ? 'Analysing…' : 'Drop an SBOM to consult'}</div>
          CycloneDX / SPDX · JSON · we analyse it and forget it
        </div>
        {error && <div className="hint" style={{ color: 'var(--danger, #c0392b)', marginTop: 10 }}>{error}</div>}
      </Card>

      {result && (
        <Card className="funnel-card">
          <SectionHead
            title={`Last consult — ${result.name}`}
            aside="ephemeral result · expires when you leave"
          />
          <div className="funnel">
            <div className="stage">
              <div className="fig mono">{result.scanned}</div>
              <div className="lbl">Matched</div>
              <div className="bar b1"><i /></div>
            </div>
            <div className="arrow"><div className="r"><b>{pct(result.applicable, result.scanned)}</b></div></div>
            <div className="stage">
              <div className="fig mono">{result.applicable}</div>
              <div className="lbl">Applicable</div>
              <div className="bar b2"><i /></div>
            </div>
            <div className="arrow"><div className="r"><b>{pct(result.kev, result.applicable)}</b></div></div>
            <div className="stage final">
              <div className="fig mono">{result.kev}</div>
              <div className="lbl">KEV-flagged</div>
              <div className="bar b3"><i /></div>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <SectionHead title="What we found" aside="KEV first, then by EPSS" />
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>Component</th>
                  <th>Severity</th>
                  <th>Signals</th>
                  <th className="r">EPSS</th>
                </tr>
              </thead>
              <tbody>
                {result.findings.slice(0, 25).map((f) => (
                  <tr key={`${f.cve}-${f.component}`}>
                    <td className="mono">{f.cve}</td>
                    <td className="mono">{f.component}</td>
                    <td><SeverityBadge kind={f.severity} /></td>
                    <td>
                      {f.isKev && <Badge kind="kev">[K] KEV</Badge>}
                      {f.art14 && <> <Badge kind="med">Art.14</Badge></>}
                      {f.highEpss && <Badge kind="med">high EPSS</Badge>}
                    </td>
                    <td className="mono r">{f.epss.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="foot-note" style={{ textAlign: 'left' }}>
            KEVs first, then applicable CVEs by descending EPSS
            {result.findings.length > 25 ? ` · showing top 25 of ${result.findings.length}` : ''}
          </div>

          <div className="funnel-foot">
            <span>Want to track this over time?</span>
            <Btn variant="primary" sm onClick={() => navigate(`/app/products/new?name=${encodeURIComponent(result.name)}&fromScan=1`)}>
              Add as a product →
            </Btn>
          </div>
        </Card>
      )}

      <KbCatalog />
    </>
  );
}

// ── Queryable CVE/KEV catalog (user request 2026-07-22) ──────────────────────
const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
function severityKind(s: string | null): StatusKind {
  const v = (s ?? '').toLowerCase();
  return v === 'critical' || v === 'high' || v === 'medium' || v === 'low' ? (v as StatusKind) : 'indeterminate';
}
type SortKey = 'epss' | 'cvss' | 'cve' | 'severity';

/**
 * Query the knowledge base: browse the CISA KEV catalog (paginated, EPSS-desc) by
 * default; enter a CVE/GHSA id to look up ANY vulnerability live (OSV); or type a
 * package name to search OSV across ecosystems. Every row shows which packages +
 * versions are affected. Server-driven: search + pagination hit /api/kb.
 */
function KbCatalog() {
  const { session } = useSession();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('epss');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  // Debounce the box → server refetch; a new query resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setSubmitted(q.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const kb = useResource<KbPage>(session, `/api/kb?q=${encodeURIComponent(submitted)}&page=${page}&size=50`);
  const data = kb.data;

  // Sort the CURRENT page client-side (the server sorts the full set; this reorders
  // what's on screen when you click a header).
  const rows = useMemo(() => {
    const list = data?.rows ?? [];
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'epss') cmp = (a.epss ?? -1) - (b.epss ?? -1);
      else if (sortKey === 'cvss') cmp = (a.cvss ?? -1) - (b.cvss ?? -1);
      else if (sortKey === 'cve') cmp = a.cveId.localeCompare(b.cveId);
      else cmp = (SEV_RANK[(a.severity ?? '').toLowerCase()] ?? 0) - (SEV_RANK[(b.severity ?? '').toLowerCase()] ?? 0);
      return dir === 'asc' ? cmp : -cmp;
    });
  }, [data?.rows, sortKey, dir]);

  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const mode = data?.mode ?? 'catalog';

  function sortBy(key: SortKey) {
    if (sortKey === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setDir(key === 'cve' ? 'asc' : 'desc'); }
  }
  const arrow = (key: SortKey) => (sortKey === key ? (dir === 'asc' ? ' ▲' : ' ▼') : '');
  const th = (key: SortKey, label: string, right = false) => (
    <th className={right ? 'r' : undefined} data-testid={`kb-sort-${key}`} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => sortBy(key)}>{label}{arrow(key)}</th>
  );

  const context = !submitted
    ? `CISA KEV catalog · ${total} known exploited vulnerabilities`
    : mode === 'cve'
      ? `Lookup ${submitted.toUpperCase()} · ${total} result${total === 1 ? '' : 's'}`
      : `Search “${submitted}” · ${total} CVE${total === 1 ? '' : 's'} (CISA KEV + live OSV)`;

  return (
    <Card style={{ padding: 18, marginTop: 16 }}>
      <SectionHead title="Vulnerability catalog" aside={<span className="hint">any CVE ID or a package · click a column to sort</span>} />
      <div className="filters" style={{ marginTop: 8 }}>
        <div className="search" style={{ flex: 1 }}><span>⌕</span>
          <input placeholder="Enter a CVE ID (e.g. CVE-2023-4863) or a package name (e.g. openssl, freertos)…" data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <div className="foot-note" data-testid="kb-context" style={{ textAlign: 'left', margin: '8px 0 0' }}>
        {context}{data?.degraded ? ' · live lookup unavailable — showing local catalog matches' : ''}
      </div>
      {kb.loading && !data ? <Loading /> : kb.error ? (
        <div className="hint" style={{ color: 'var(--danger, #c0392b)', marginTop: 10 }}>Couldn’t load: {kb.error}</div>
      ) : (
        <>
          <div className="tablewrap" style={{ marginTop: 10 }}>
            <table>
              <thead>
                <tr>
                  {th('cve', 'CVE')}
                  <th>Affected packages / versions</th>
                  {th('severity', 'Severity')}
                  <th>Signals</th>
                  {th('cvss', 'CVSS', true)}
                  {th('epss', 'EPSS', true)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="hint" style={{ padding: 18, textAlign: 'center' }}>{submitted ? `No results for “${submitted}”.` : 'Catalog is empty.'}</td></tr>}
                {rows.map((v) => (
                  <tr key={v.id} data-testid="kb-row">
                    <td className="mono">{v.cveId}</td>
                    <td>
                      {v.affected.length === 0 ? <span className="hint">—</span> : v.affected.map((a, i) => (
                        <div key={i} className="mono" style={{ fontSize: 'calc(12px*var(--fs))' }}>
                          {a.product}{a.versions ? <span style={{ color: 'var(--muted)' }}> · {a.versions}</span> : null}
                        </div>
                      ))}
                      {v.affectedMore > 0 && <div className="hint" style={{ fontSize: 'calc(11px*var(--fs))' }}>+{v.affectedMore} more package(s)</div>}
                    </td>
                    <td><SeverityBadge kind={severityKind(v.severity)} /></td>
                    <td>
                      {v.isKev && <Badge kind="kev">[K] KEV</Badge>}
                      {v.source === 'osv' && <> <Badge kind="out">via OSV</Badge></>}
                      {(v.epss ?? 0) >= 0.5 && <> <Badge kind="med">high EPSS</Badge></>}
                    </td>
                    <td className="mono r">{v.cvss != null ? v.cvss.toFixed(1) : '—'}</td>
                    <td className="mono r">{v.epss != null ? v.epss.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="funnel-foot" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 }}>
              <Btn sm disabled={page <= 1} data-testid="kb-prev" onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</Btn>
              <span className="hint" data-testid="kb-pageinfo">Page {page} of {pages}</span>
              <Btn sm disabled={page >= pages} data-testid="kb-next" onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next →</Btn>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
